import type { AI_MODE } from "@/features/ai-agent/types/ai-mode";

export type ClosingNoteFieldConfigOption =
  | "mandatory"
  | "optional"
  | "disabled";

export type ClosingNoteConfig = {
  category: ClosingNoteFieldConfigOption;
  summary: ClosingNoteFieldConfigOption;
};

export type DayOfWeek =
  | "monday"
  | "tuesday"
  | "wednesday"
  | "thursday"
  | "friday"
  | "saturday"
  | "sunday";

export type WorkingInterval = {
  start: string; // "09:00" (HH:mm format), always before `end`
  end: string; // "17:00"
};

/** An empty array shuts that weekday. */
export type WorkingHoursConfig = Record<DayOfWeek, WorkingInterval[]>;

export type InboxOffDay = {
  date: string; // ISO date YYYY-MM-DD
  reason?: string;
};

export type PendingWorkingHours = {
  effectiveFrom: string; // ISO date YYYY-MM-DD
  hours: WorkingHoursConfig;
};

export type ContactFieldLayoutConfig = {
  order: string[];
  pinned: string[];
};

/** Wire values - they are the Postgres enum and the `sla_alerts.clock` column. */
export const SLA_CLOCK = {
  FIRST_REPLY: "first_reply",
  SUBSEQUENT_REPLY: "subsequent_reply",
  OPEN_CONVERSATION: "open_conversation",
  FOLLOW_UP_OVERDUE: "follow_up_overdue",
  AI_WAITING: "ai_waiting",
  UNASSIGNED: "unassigned",
} as const;

export type SlaClock = (typeof SLA_CLOCK)[keyof typeof SLA_CLOCK];

export const SLA_DURATION_UNIT = {
  MINUTES: "minutes",
  HOURS: "hours",
  DAYS: "days",
} as const;

export type SlaDurationUnit =
  (typeof SLA_DURATION_UNIT)[keyof typeof SLA_DURATION_UNIT];

export const SLA_AUDIENCE = {
  ASSIGNEE: "assignee",
  /** Every member of the workspace, whatever their role. */
  WORKSPACE_USERS: "workspace_users",
  BOTH: "both",
} as const;

export type SlaAudience = (typeof SLA_AUDIENCE)[keyof typeof SLA_AUDIENCE];

/** One row of `GET /workspaces/:id/slas`. A clock nobody configured has no row. */
export type SlaClockConfig = {
  clock: SlaClock;
  enabled: boolean;
  durationValue: number;
  durationUnit: SlaDurationUnit;
  /** Server-derived from value × unit; never sent back. */
  durationSeconds: number;
  /** Off keeps the percent and audience below for next time. */
  notifyOnReminder: boolean;
  reminderPercent: number;
  reminderAudience: SlaAudience;
  /** Empty means everyone in the pool, which stays right as the team changes. */
  reminderUserIds: string[];
  /** Off still records the breach - it only stops anyone being told. */
  notifyOnBreach: boolean;
  breachAudience: SlaAudience;
  breachUserIds: string[];
};

export type SlaClockConfigInput = Omit<SlaClockConfig, "durationSeconds">;

/** The AI a workspace gets, picked for it in admin. Keys MUST match the backend
 *  `AiFeature` enum. */
export const AI_FEATURE = {
  ECHO: "echo",
  SUMMARY: "summary",
  WRITING: "writing",
  COPILOT: "copilot",
  CLOSING_NOTES: "closingNotes",
  DICTATION: "dictation",
} as const;

export type AiFeature = (typeof AI_FEATURE)[keyof typeof AI_FEATURE];

/** What a workspace gets before admin picks anything - mirrors the backend. */
export const DEFAULT_AI_FEATURES: readonly AiFeature[] = [AI_FEATURE.SUMMARY];

export type WorkspaceConfig = {
  aiEnabled?: boolean;
  /** What Echo switches to on a conversation a teammate takes over. Unset
   *  means off. */
  aiOnTakeOver?: typeof AI_MODE.DRAFTS | typeof AI_MODE.OFF;
  /** Conversation summaries. Unset means on; only false switches them off. */
  aiSummaries?: boolean;
  closingNote?: ClosingNoteConfig;
  isOnboardingCompleted?: boolean;
  /** Explicitly hidden from the dashboard; the sidebar entry stays until completed. */
  isOnboardingHidden?: boolean;
  contactFields?: ContactFieldLayoutConfig;
  /** Days after a follow-up is done that a new booking still counts as won by
   *  it. Unset means `DEFAULT_FOLLOW_UP_CREDIT_WINDOW_DAYS`. */
  followUpCreditWindowDays?: number;
};

export type Workspace = {
  id: string;
  name: string;
  organizationId: string;
  createdAt: Date;
  updatedAt: Date;
  config?: WorkspaceConfig;
  /** Picked in admin, read-only here; `config.aiEnabled` still switches all
   *  of them off. Absent only from an older backend. */
  aiFeatures?: AiFeature[];
  /** null = never configured, which counts as open around the clock. */
  workingHours: WorkingHoursConfig | null;
  offDays: InboxOffDay[];
  /** May be the literal `-infinity` for a migrated or first schedule; that
   *  means "always", and must never be rendered to a user as-is. */
  workingHoursEffectiveFrom?: string | null;
  pendingWorkingHours?: PendingWorkingHours | null;
};
